import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";

const stats = [
  { icon: "ri-shield-check-line", value: "99.9%", label: "발송 성공률" },
  { icon: "ri-flashlight-line", value: "0.3초", label: "평균 발송 속도" },
  { icon: "ri-bar-chart-2-line", value: "50만+", label: "일일 발송량" },
  { icon: "ri-group-line", value: "10,000+", label: "활성 사용자" },
];

export default function HeroSection() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    canvas.width = canvas.offsetWidth;
    canvas.height = canvas.offsetHeight;

    const particles: { x: number; y: number; vx: number; vy: number; size: number; opacity: number }[] = [];
    for (let i = 0; i < 60; i++) {
      particles.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        vx: (Math.random() - 0.5) * 0.4,
        vy: (Math.random() - 0.5) * 0.4,
        size: Math.random() * 2 + 0.5,
        opacity: Math.random() * 0.5 + 0.1,
      });
    }

    let animId: number;
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particles.forEach((p) => {
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < 0 || p.x > canvas.width) p.vx *= -1;
        if (p.y < 0 || p.y > canvas.height) p.vy *= -1;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(167, 139, 250, ${p.opacity})`;
        ctx.fill();
      });
      animId = requestAnimationFrame(animate);
    };
    animate();
    return () => cancelAnimationFrame(animId);
  }, []);

  return (
    <section className="relative min-h-screen bg-[#0a0a18] flex flex-col items-center justify-center overflow-hidden pt-16">
      {/* Animated canvas background */}
      <canvas ref={canvasRef} className="absolute inset-0 w-full h-full pointer-events-none" />

      {/* Gradient orbs */}
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-violet-600/20 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-1/3 right-1/4 w-80 h-80 bg-pink-600/15 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-violet-900/10 rounded-full blur-3xl pointer-events-none" />

      {/* Content */}
      <div className="relative z-10 w-full max-w-4xl mx-auto px-6 text-center">
        <div className="inline-flex items-center gap-2 bg-white/5 border border-white/10 rounded-full px-4 py-1.5 mb-8">
          <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse inline-block"></span>
          <span className="text-gray-300 text-xs">3사 통신망 연동 서비스</span>
        </div>

        <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-tight mb-6">
          빠르고 안정적인
          <br />
          <span className="bg-gradient-to-r from-violet-400 via-pink-400 to-violet-400 bg-clip-text text-transparent">
            문자 발송 서비스
          </span>
        </h1>

        <p className="text-gray-400 text-lg md:text-xl mb-10 leading-relaxed max-w-2xl mx-auto">
          SMS, LMS, MMS를 합리적인 가격으로 발송하세요.
          <br />
          3사 통신망 연동으로 99.9% 발송 성공률을 보장합니다.
        </p>

        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-20">
          {user ? (
            <>
              <button
                onClick={() => navigate("/dashboard")}
                className="w-full sm:w-auto bg-gradient-to-r from-violet-600 to-pink-600 hover:from-violet-500 hover:to-pink-500 text-white px-8 py-3.5 rounded-lg font-semibold text-base transition-all hover:scale-105 cursor-pointer whitespace-nowrap flex items-center gap-2 justify-center"
              >
                대시보드 바로가기
                <i className="ri-arrow-right-line"></i>
              </button>
              <button
                onClick={() => navigate("/dashboard/send")}
                className="w-full sm:w-auto border border-white/20 hover:border-white/40 text-white px-8 py-3.5 rounded-lg font-medium text-base transition-all hover:bg-white/5 cursor-pointer whitespace-nowrap flex items-center gap-2 justify-center"
              >
                문자 발송하기
                <i className="ri-send-plane-line"></i>
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => navigate("/login")}
                className="w-full sm:w-auto bg-gradient-to-r from-violet-600 to-pink-600 hover:from-violet-500 hover:to-pink-500 text-white px-8 py-3.5 rounded-lg font-semibold text-base transition-all hover:scale-105 cursor-pointer whitespace-nowrap flex items-center gap-2 justify-center"
              >
                무료로 시작하기
                <i className="ri-arrow-right-line"></i>
              </button>
              <button
                onClick={() => navigate("/login")}
                className="w-full sm:w-auto border border-white/20 hover:border-white/40 text-white px-8 py-3.5 rounded-lg font-medium text-base transition-all hover:bg-white/5 cursor-pointer whitespace-nowrap"
              >
                로그인
              </button>
            </>
          )}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {stats.map((stat) => (
            <div
              key={stat.label}
              className="bg-white/5 border border-white/10 rounded-xl p-5 hover:bg-white/8 transition-colors"
            >
              <div className="w-8 h-8 flex items-center justify-center mb-3 mx-auto">
                <i className={`${stat.icon} text-violet-400 text-xl`}></i>
              </div>
              <div className="text-2xl md:text-3xl font-bold text-white mb-1">{stat.value}</div>
              <div className="text-gray-400 text-sm">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}